import React from 'react'
import { DocumentTextIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import SEO from '@components/common/SEO'

const TermsPage: React.FC = () => {
  const lastUpdated = '1 ตุลาคม 2025'

  const termsStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "ข้อกำหนดและเงื่อนไขการใช้บริการ - ไทยเซ็กคิวริตี้ไดเรกทอรี่",
    "description": "ข้อกำหนดและเงื่อนไขการใช้บริการของไทยเซ็กคิวริตี้ไดเรกทอรี่",
    "dateModified": "2025-10-01"
  }

  return (
    <>
      <SEO
        title="ข้อกำหนดและเงื่อนไขการใช้บริการ"
        description="ข้อกำหนดและเงื่อนไขการใช้บริการของไทยเซ็กคิวริตี้ไดเรกทอรี่ - กรุณาอ่านและทำความเข้าใจก่อนใช้บริการ"
        keywords="ข้อกำหนดการใช้งาน, terms of service, เงื่อนไข, กฎระเบียบ"
      />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(termsStructuredData) }}
      />

      <div className="bg-gradient-to-b from-primary-50 to-white min-h-screen">
        {/* Hero Section */}
        <div className="bg-primary-600 text-white py-16">
          <div className="container-wrapper">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <DocumentTextIcon className="w-16 h-16" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                ข้อกำหนดและเงื่อนไขการใช้บริการ
              </h1>
              <p className="text-xl text-primary-100">
                กรุณาอ่านข้อกำหนดนี้อย่างละเอียดก่อนใช้บริการของเรา
              </p>
              <p className="text-sm text-primary-200 mt-4">
                อัปเดตล่าสุด: {lastUpdated}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container-wrapper py-12">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 mb-8">
              <p className="text-neutral-700 leading-relaxed">
                ยินดีต้อนรับสู่ไทยเซ็กคิวริตี้ไดเรกทอรี่ ข้อกำหนดและเงื่อนไขนี้ควบคุมการใช้งานเว็บไซต์และบริการของเรา
                โดยการเข้าถึงหรือใช้บริการ หมายความว่าคุณยอมรับและตกลงที่จะผูกพันตามข้อกำหนดเหล่านี้
                หากคุณไม่ยอมรับข้อกำหนดใดๆ กรุณาหยุดการใช้บริการทันที
              </p>
            </div>

            {/* Acceptance Notice */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-8 rounded-r-lg">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-amber-900 mb-2">ข้อควรทราบสำคัญ</h3>
                  <p className="text-amber-800 text-sm leading-relaxed">
                    การใช้บริการนี้ถือว่าคุณได้อ่านและยอมรับข้อกำหนดทั้งหมดแล้ว
                    กรุณาอ่านอย่างละเอียดและติดต่อเราหากมีข้อสงสัย
                  </p>
                </div>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-8">
              {/* Section 1 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  1. การยอมรับข้อกำหนด
                </h2>

                <div className="space-y-3 text-neutral-700 leading-relaxed">
                  <p>
                    โดยการเข้าถึงและใช้งานเว็บไซต์ไทยเซ็กคิวริตี้ไดเรกทอรี่ ("บริการ") คุณตกลงที่จะผูกพันตามข้อกำหนดและเงื่อนไขนี้
                    รวมถึง:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>นโยบายความเป็นส่วนตัว</li>
                    <li>กฎระเบียบการใช้งานที่เกี่ยวข้อง</li>
                    <li>การแก้ไขและเปลี่ยนแปลงในอนาคต</li>
                  </ul>
                  <p className="mt-3">
                    หากคุณไม่ยอมรับข้อกำหนดเหล่านี้ กรุณาหยุดการใช้บริการทันที
                  </p>
                </div>
              </div>

              {/* Section 2 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  2. คำนิยาม
                </h2>

                <div className="space-y-2 text-neutral-700 leading-relaxed">
                  <ul className="space-y-2">
                    <li>
                      <strong>"เรา" "พวกเรา" "บริษัท"</strong> หมายถึง ไทยเซ็กคิวริตี้ไดเรกทอรี่
                    </li>
                    <li>
                      <strong>"คุณ" "ผู้ใช้งาน"</strong> หมายถึง บุคคลที่เข้าถึงหรือใช้บริการของเรา
                    </li>
                    <li>
                      <strong>"บริการ"</strong> หมายถึง เว็บไซต์ แพลตฟอร์ม และบริการที่เกี่ยวข้องทั้งหมด
                    </li>
                    <li>
                      <strong>"เนื้อหา"</strong> หมายถึง ข้อมูล ข้อความ รูปภาพ และสื่อต่างๆ ที่แสดงบนเว็บไซต์
                    </li>
                    <li>
                      <strong>"บัญชี"</strong> หมายถึง บัญชีผู้ใช้ที่ลงทะเบียนไว้กับเรา
                    </li>
                  </ul>
                </div>
              </div>

              {/* Section 3 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  3. การใช้งานบริการ
                </h2>

                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">3.1 สิทธิ์การใช้งาน</h3>
                    <p>
                      เราอนุญาตให้คุณใช้บริการตามข้อกำหนดนี้ สำหรับวัตถุประสงค์ส่วนบุคคลและไม่ใช่เชิงพาณิชย์
                      คุณสามารถ:
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                      <li>ค้นหาและดูข้อมูลบริษัทรักษาความปลอดภัย</li>
                      <li>สมัครสมาชิกและใช้ฟีเจอร์ที่ให้บริการ</li>
                      <li>Claim บริษัทของคุณ (ตามเงื่อนไข)</li>
                      <li>ติดต่อบริษัทผ่านข้อมูลที่แสดง</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">3.2 ข้อห้ามในการใช้งาน</h3>
                    <p>คุณตกลงที่จะ <strong>ไม่</strong> ทำสิ่งต่อไปนี้:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                      <li>ละเมิดกฎหมาย หรือสิทธิ์ของผู้อื่น</li>
                      <li>แอบอ้างเป็นบุคคลอื่น หรือให้ข้อมูลเท็จ</li>
                      <li>ใช้บริการเพื่อส่งสแปม หรือโฆษณาที่ไม่ได้รับอนุญาต</li>
                      <li>คัดลอกข้อมูลด้วยระบบอัตโนมัติ (scraping/crawling) โดยไม่ได้รับอนุญาต</li>
                      <li>พยายามเจาะระบบหรือทำลายความปลอดภัย</li>
                      <li>อัปโหลดไวรัส มัลแวร์ หรือโค้ดที่เป็นอันตราย</li>
                      <li>ใช้บริการในทางที่ผิดกฎหมายหรือไม่เหมาะสม</li>
                      <li>รบกวนหรือขัดขวางการทำงานของบริการ</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">3.3 อายุขั้นต่ำ</h3>
                    <p>
                      คุณต้องมีอายุอย่างน้อย <strong>18 ปีบริบูรณ์</strong> เพื่อสมัครสมาชิกและใช้บริการบางส่วน
                      หากคุณอายุต่ำกว่านี้ ต้องได้รับความยินยอมจากผู้ปกครอง
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 4 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  4. บัญชีผู้ใช้และความปลอดภัย
                </h2>

                <div className="space-y-3 text-neutral-700 leading-relaxed">
                  <p>เมื่อคุณสร้างบัญชี คุณต้อง:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>ให้ข้อมูลที่ถูกต้อง ครบถ้วน และเป็นปัจจุบัน</li>
                    <li>รักษาความปลอดภัยของรหัสผ่านและข้อมูลบัญชี</li>
                    <li>รับผิดชอบต่อกิจกรรมทั้งหมดที่เกิดขึ้นภายใต้บัญชีของคุณ</li>
                    <li>แจ้งเราทันทีหากพบการเข้าถึงโดยไม่ได้รับอนุญาต</li>
                  </ul>
                  <p className="mt-3">
                    <strong>เราขอสงวนสิทธิ์</strong>ในการระงับ ปิด หรือลบบัญชีที่ละเมิดข้อกำหนดนี้ โดยไม่ต้องแจ้งล่วงหน้า
                  </p>
                </div>
              </div>

              {/* Section 5 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  5. การ Claim บริษัท
                </h2>

                <div className="space-y-3 text-neutral-700 leading-relaxed">
                  <p>
                    หากคุณต้องการ Claim บริษัทในฐานะเจ้าของหรือผู้มีสิทธิ์จัดการ:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>คุณต้องเป็นเจ้าของหรือผู้ได้รับมอบหมายอย่างถูกต้องตามกฎหมาย</li>
                    <li>ต้องส่งเอกสารยืนยันตัวตน (ใบทะเบียนบริษัท, บัตรประชาชน, หนังสือมอบอำนาจ)</li>
                    <li>ข้อมูลที่ให้ต้องถูกต้อง ครบถ้วน และเป็นปัจจุบัน</li>
                    <li>การ Claim ทุกครั้งต้องผ่านการอนุมัติจาก Admin</li>
                    <li>เราขอสงวนสิทธิ์ในการปฏิเสธ Claim ที่ไม่มีหลักฐานเพียงพอ</li>
                  </ul>
                  <p className="mt-3 text-amber-700 font-semibold">
                    การให้ข้อมูลเท็จเพื่อ Claim บริษัทผิดกฎหมาย และอาจถูกดำเนินคดีตามกฎหมาย
                  </p>
                </div>
              </div>

              {/* Section 6 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  6. ทรัพย์สินทางปัญญา
                </h2>

                <div className="space-y-3 text-neutral-700 leading-relaxed">
                  <p>
                    เนื้อหาทั้งหมดบนเว็บไซต์ (ข้อความ รูปภาพ โลโก้ ซอฟต์แวร์ โค้ด) เป็นทรัพย์สินของเรา
                    และได้รับความคุ้มครองตามกฎหมายลิขสิทธิ์และทรัพย์สินทางปัญญา
                  </p>
                  <p>
                    <strong>คุณไม่สามารถ:</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>คัดลอก ดัดแปลง หรือจำหน่ายเนื้อหาโดยไม่ได้รับอนุญาต</li>
                    <li>ใช้โลโก้หรือเครื่องหมายการค้าของเราโดยไม่ได้รับอนุญาต</li>
                    <li>Reverse engineer หรือถอดรหัสซอฟต์แวร์</li>
                  </ul>
                  <p className="mt-3">
                    ข้อมูลบริษัทที่ผู้ใช้อัปโหลด (โลโก้ รูปภาพ ข้อความ) ยังคงเป็นทรัพย์สินของเจ้าของ
                    แต่คุณอนุญาตให้เราแสดงและใช้งานบนแพลตฟอร์ม
                  </p>
                </div>
              </div>

              {/* Section 7 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  7. ข้อจำกัดความรับผิดชอบ
                </h2>

                <div className="space-y-3 text-neutral-700 leading-relaxed">
                  <p>
                    <strong>บริการนี้ให้บริการ "ตามสภาพที่เป็น" (AS-IS) โดยไม่มีการรับประกันใดๆ</strong>
                  </p>
                  <p>
                    เราไม่รับผิดชอบต่อ:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>ความถูกต้องหรือความครบถ้วนของข้อมูลบริษัท</li>
                    <li>ความเสียหายใดๆ ที่เกิดจากการใช้หรือไม่สามารถใช้บริการ</li>
                    <li>การกระทำของบริษัทที่ลงทะเบียนในระบบ</li>
                    <li>ความสูญหายของข้อมูลหรือกำไร</li>
                    <li>การหยุดชะงักของบริการ (downtime)</li>
                  </ul>
                  <p className="mt-3 font-semibold">
                    ความรับผิดสูงสุดของเราต่อคุณจำกัดอยู่ที่ 1,000 บาท หรือค่าบริการที่คุณจ่ายให้เราในช่วง 6 เดือนที่ผ่านมา
                    แล้วแต่จำนวนใดจะสูงกว่า
                  </p>
                </div>
              </div>

              {/* Section 8 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  8. การชดใช้ค่าเสียหาย (Indemnification)
                </h2>

                <div className="text-neutral-700 leading-relaxed">
                  <p>
                    คุณตกลงที่จะชดใช้ค่าเสียหาย และปกป้องเรา พนักงาน และหุ้นส่วนของเรา
                    จากการเรียกร้อง ค่าเสียหาย หรือค่าใช้จ่ายใดๆ ที่เกิดจาก:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                    <li>การละเมิดข้อกำหนดนี้</li>
                    <li>การละเมิดสิทธิ์ของบุคคลที่สาม</li>
                    <li>การใช้บริการอย่างผิดกฎหมาย</li>
                  </ul>
                </div>
              </div>

              {/* Section 9 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  9. การเปลี่ยนแปลงบริการและข้อกำหนด
                </h2>

                <div className="space-y-3 text-neutral-700 leading-relaxed">
                  <p>
                    เราขอสงวนสิทธิ์ในการ:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>แก้ไข เปลี่ยนแปลง หรือยกเลิกบริการใดๆ ได้ตลอดเวลา</li>
                    <li>อัปเดตข้อกำหนดนี้เป็นครั้งคราว (จะแจ้งให้ทราบล่วงหน้า)</li>
                    <li>ปฏิเสธการให้บริการแก่ผู้ใช้ที่ละเมิดข้อกำหนด</li>
                  </ul>
                  <p className="mt-3">
                    การเปลี่ยนแปลงสำคัญจะแจ้งผ่านอีเมลหรือประกาศบนเว็บไซต์อย่างน้อย 30 วันล่วงหน้า
                  </p>
                </div>
              </div>

              {/* Section 10 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  10. การยกเลิกบัญชี
                </h2>

                <div className="space-y-3 text-neutral-700 leading-relaxed">
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">10.1 การยกเลิกโดยคุณ</h3>
                    <p>
                      คุณสามารถยกเลิกบัญชีได้ทุกเมื่อผ่านการตั้งค่าบัญชี หรือติดต่อเรา
                      ข้อมูลส่วนตัวจะถูกลบตามนโยบายความเป็นส่วนตัว
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">10.2 การยกเลิกโดยเรา</h3>
                    <p>
                      เราสามารถระงับหรือยกเลิกบัญชีของคุณได้ทันที หากคุณ:
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                      <li>ละเมิดข้อกำหนดนี้</li>
                      <li>ให้ข้อมูลเท็จหรือหลอกลวง</li>
                      <li>ใช้บริการอย่างผิดกฎหมาย</li>
                      <li>ไม่ได้ใช้งานมากกว่า 2 ปี</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 11 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  11. กฎหมายที่ใช้บังคับและการระงับข้อพิพาท
                </h2>

                <div className="text-neutral-700 leading-relaxed">
                  <p>
                    ข้อกำหนดนี้อยู่ภายใต้กฎหมายไทย ข้อพิพาทใดๆ จะยุติโดยการไกล่เกลี่ย
                    หากไม่สำเร็จจะนำไปสู่ศาลที่มีอำนาจในประเทศไทย
                  </p>
                </div>
              </div>

              {/* Section 12 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  12. ข้อกำหนดทั่วไป
                </h2>

                <div className="space-y-2 text-neutral-700 leading-relaxed">
                  <ul className="list-disc list-inside space-y-2">
                    <li>
                      <strong>ความสมบูรณ์:</strong> ข้อกำหนดนี้เป็นข้อตกลงที่สมบูรณ์ระหว่างคุณและเรา
                    </li>
                    <li>
                      <strong>การสละสิทธิ์:</strong> การที่เราไม่บังคับใช้สิทธิ์ใดๆ ไม่ถือว่าสละสิทธิ์นั้น
                    </li>
                    <li>
                      <strong>ความแยกส่วนได้:</strong> หากข้อกำหนดข้อใดไม่สามารถบังคับใช้ได้ ข้ออื่นยังคงมีผลบังคับ
                    </li>
                    <li>
                      <strong>การโอนสิทธิ์:</strong> คุณไม่สามารถโอนสิทธิ์ให้ผู้อื่นได้ เราสามารถโอนได้
                    </li>
                  </ul>
                </div>
              </div>

              {/* Section 13 */}
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4 border-l-4 border-primary-600 pl-4">
                  13. การติดต่อเรา
                </h2>

                <div className="text-neutral-700 leading-relaxed space-y-3">
                  <p>
                    หากคุณมีคำถามเกี่ยวกับข้อกำหนดนี้ กรุณาติดต่อ:
                  </p>
                  <div className="bg-neutral-50 p-4 rounded-lg">
                    <p><strong>ไทยเซ็กคิวริตี้ไดเรกทอรี่</strong></p>
                    <p className="mt-2">
                      <strong>อีเมล:</strong> <a href="mailto:info@koder3.com" className="text-primary-600 hover:text-primary-700 underline">info@koder3.com</a>
                    </p>
                    <p>
                      <strong>เว็บไซต์:</strong> <a href="https://thaisecurity.directory" className="text-primary-600 hover:text-primary-700 underline">https://thaisecurity.directory</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Acceptance Box */}
            <div className="mt-8 bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-green-900 mb-2">การยอมรับข้อกำหนด</h3>
                  <p className="text-green-800 text-sm leading-relaxed">
                    โดยการใช้บริการนี้ต่อไป คุณได้อ่านและยอมรับข้อกำหนดและเงื่อนไขทั้งหมดแล้ว
                    หากไม่ยอมรับ กรุณาหยุดการใช้บริการทันที
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-lg p-8 text-white text-center">
              <h2 className="text-2xl font-bold mb-3">
                มีคำถามเกี่ยวกับข้อกำหนด?
              </h2>
              <p className="text-primary-100 mb-6">
                ติดต่อทีมกฎหมายของเราได้ตลอดเวลา เรายินดีให้คำชี้แจง
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors duration-200 shadow-md"
                >
                  ติดต่อเรา
                </a>
                <a
                  href="/privacy"
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-400 transition-colors duration-200"
                >
                  นโยบายความเป็นส่วนตัว
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default TermsPage
